import * as TFF from '@tff/types/tff';

export interface RefundCalculationResult {
  totalFlightAmounts: number;
  totalSeatReservationAmount: number;
  totalAncillariesAmounts: number;
  seatReservationsByJourney: string[][];
}

/**
 * Calculates refund amounts for selected journeys including flight fares, seat reservations, and other ancillaries.
 * 
 * @param journeysToRender - Array of journeys to calculate refunds for
 * @param fares - Record of fares containing fare products mapped by journey Ond
 * @param ancillaries - Optional record of ancillaries (seats, baggage, meals, etc.) mapped by journey Ond
 * @returns Object containing total refund amounts and seat reservations grouped by journey index
 * 
 * @example
 * ```typescript
 * const result = calculateRefundAmountsForJourneys(
 *   [{ Ond: 'TXLPMIDXB' }],
 *   { fare1: { FareProducts: [{ JourneyOnd: 'TXLPMIDXB', BaseAmount: 100 }] } },
 *   { anc1: { JourneyOnd: 'TXLPMIDXB', Type: 'SEAT', TotalAmount: 20, Value: '12A' } }
 * );
 * // Returns: { totalFlightAmounts: 100, totalSeatReservationAmount: 20, totalAncillariesAmounts: 0, seatReservationsByJourney: [['12A']] }
 * ```
 */
export const calculateRefundAmountsForJourneys = (
  journeysToRender: TFF.Journey[],
  fares: Record<string, TFF.Fare>,
  ancillaries?: Record<string, TFF.Ancillary>,
): RefundCalculationResult => {
  let totalFlightAmounts = 0;
  let totalAncillariesAmounts = 0;
  let totalSeatReservationAmount = 0;
  let seatReservationsByJourney: string[][] = [];

  const allFares = Object.values(fares);

  journeysToRender?.forEach((j, i) => {
    allFares.forEach(f => {
      f.FareProducts?.forEach(fp => {
        if (fp.JourneyOnd === j.Ond) {
          totalFlightAmounts += fp.BaseAmount!;
        }
      });
    });

    if (ancillaries) {
      Object.values(ancillaries)
        .filter(ancillary => ancillary.JourneyOnd === j.Ond)
        .forEach(ancillary => {
          const amount = ancillary.TotalAmount ?? 0;
          if (ancillary.Type === 'SEAT') {
            seatReservationsByJourney[i] = seatReservationsByJourney[i] ?? [];
            seatReservationsByJourney[i].push(ancillary.Value);
            totalSeatReservationAmount += amount;
          } else {
            totalAncillariesAmounts += amount;
          }
        });
    }
  });

  return {
    totalFlightAmounts,
    totalSeatReservationAmount,
    totalAncillariesAmounts,
    seatReservationsByJourney,
  };
};

/**
 * Counts the number of passengers by type (Adult, Child, Infant).
 * 
 * @param passengers - Record of passengers with their type information
 * @returns Object with counts for each passenger type (ADT, CHD, INF)
 * 
 * @example
 * ```typescript
 * const result = calculateNumberOfPaxType({
 *   pax1: { Type: 'ADT' },
 *   pax2: { Type: 'ADT' },
 *   pax3: { Type: 'CHD' }
 * });
 * // Returns: { ADT: 2, CHD: 1, INF: 0 }
 * ```
 */
export const calculateNumberOfPaxType = (
  passengers: Record<string, TFF.Passenger>,
): { ADT: number; CHD: number; INF: number } => {
  return Object.values(passengers).reduce(
    (acc, passenger) => {
      acc[passenger.Type]++;
      return acc;
    },
    { ADT: 0, CHD: 0, INF: 0 }
  );
};

/**
 * Formats error messages from various error types into a single string.
 * Handles both array of error objects (NDC errors) and single error objects.
 * 
 * @param error - Error object or array of error objects with DescText property
 * @returns Formatted error message string. Multiple errors are joined with newlines.
 * 
 * @example
 * ```typescript
 * // Array of errors
 * formatErrorMessage([{ DescText: 'Error 1' }, { DescText: 'Error 2' }]);
 * // Returns: 'Error 1\nError 2'
 * 
 * // Single error
 * formatErrorMessage({ message: 'Single error' });
 * // Returns: 'Single error'
 * ```
 */
export const formatErrorMessage = (error: any): string => {
  if (Array.isArray(error)) {
    return error.map(p => p.DescText).join('\n');
  }
  return error.message;
};
